import { type TranslationKey } from "../locales";
import type { BrokerCheckReasonCode, ProviderCheckResult } from "./broker-sync-panel.types";
import { redactKnownSecrets } from "./broker-sync-panel-error.util";
import { t } from "./broker-sync-panel-format.util";

export const BROKER_REASON_KEYS: Record<BrokerCheckReasonCode, TranslationKey> = {
  "secret-not-configured": "portfolio.brokerSync.reasonSecretNotConfigured",
  "invalid-credentials": "portfolio.brokerSync.reasonInvalidCredentials",
  "access-denied": "portfolio.brokerSync.reasonAccessDenied",
  "rate-limited": "portfolio.brokerSync.reasonRateLimited",
  "broker-unavailable": "portfolio.brokerSync.reasonBrokerUnavailable",
  "network": "portfolio.brokerSync.reasonNetwork",
  "statement-sections-missing": "portfolio.brokerSync.reasonStatementSections",
};

/**
 * A failed broker check in Russian. A known `reasonCode` gives the localized cause and keeps the
 * broker's own text as a detail; an unknown one shows "Ошибка брокера" with that text. The text is
 * already masked by the server and is masked again here with the secrets typed on this screen.
 */
export function describeBrokerReason(
  reason: string | undefined,
  code: BrokerCheckReasonCode | undefined,
  secrets: readonly string[],
): string {
  const detail = reason ? redactKnownSecrets(reason, secrets) : "";
  const key = code ? BROKER_REASON_KEYS[code] : undefined;
  if (key) {
    return detail && detail !== t(key)
      ? t("portfolio.brokerSync.brokerErrorWithDetail", { reason: t(key), detail })
      : t(key);
  }
  return detail ? t("portfolio.brokerSync.brokerErrorUnknown", { detail }) : t("portfolio.brokerSync.connectionFailed");
}

export function ProviderConnectionResult({ result, secrets }: { result: ProviderCheckResult; secrets: readonly string[] }) {
  const accounts = result.accounts.items ?? [];
  const operations = result.operations;
  const checkedIds = operations.checkedBrokerAccountIds ?? [];
  const failures = operations.failures ?? [];
  const reasonText = (reason: string | undefined, code?: BrokerCheckReasonCode) =>
    describeBrokerReason(reason, code, secrets);
  const skippedReason = operations.reasonCode === "accounts-unavailable"
    ? t("portfolio.brokerSync.reasonAccountsUnavailable")
    : operations.reasonCode === "no-accounts"
      ? t("portfolio.brokerSync.reasonNoAccounts")
      : null;
  return (
    <div className="space-y-2 rounded-md border p-3 text-sm" role="status">
      <h4 className="font-medium">{t("portfolio.brokerSync.brokerAccounts")}</h4>
      {accounts.length === 0 ? (
        <p>{result.accounts.ok ? t("portfolio.brokerSync.noBrokerAccounts") : t("portfolio.brokerSync.connectionFailed")}</p>
      ) : (
        <ul className="list-inside list-disc">
          {accounts.map((account) => (
            <li key={account.brokerAccountId}>
              <span className="font-mono">{account.brokerAccountId}</span>
              {account.name && account.name !== account.brokerAccountId ? ` · ${account.name}` : ""}
            </li>
          ))}
        </ul>
      )}
      {!result.accounts.ok ? (
        <p role="alert" className="text-destructive">
          {t("portfolio.brokerSync.connectionFailed")}: {reasonText(result.accounts.reason, result.accounts.reasonCode)}
        </p>
      ) : null}
      {skippedReason ? (
        <p className="text-amber-700">{skippedReason}</p>
      ) : !result.accounts.ok ? null : operations.ok ? (
        <p className="text-emerald-700">
          {t("portfolio.brokerSync.historyAvailable", { count: operations.count ?? 0 })} {t("portfolio.brokerSync.accountsChecked", { count: operations.checkedAccounts ?? 0 })}
        </p>
      ) : (
        <div className="space-y-1 text-amber-700">
          <p>
            {t("portfolio.brokerSync.connectionPartial", {
              checked: operations.checkedAccounts ?? 0,
              failed: operations.failedAccounts ?? 0,
              count: operations.count ?? 0,
            })}
          </p>
          {failures.length > 0 ? (
            <>
              <p>{t("portfolio.brokerSync.failedAccountList", { accounts: failures.map((item) => item.brokerAccountId).join(", ") })}</p>
              <ul role="alert" className="list-inside list-disc">
                {failures.map((item) => (
                  <li key={item.brokerAccountId}>
                    {t("portfolio.brokerSync.failedAccountItem", {
                      accountId: item.brokerAccountId,
                      reason: reasonText(item.reason, item.reasonCode),
                    })}
                  </li>
                ))}
              </ul>
            </>
          ) : operations.reason ? (
            <p role="alert">{reasonText(operations.reason)}</p>
          ) : null}
        </div>
      )}
      {result.accounts.ok && !skippedReason && checkedIds.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          {t("portfolio.brokerSync.checkedAccountList", { accounts: checkedIds.join(", ") })}
        </p>
      ) : null}
      {result.accounts.ok && !skippedReason ? (
        <p className="text-xs text-muted-foreground">
          {t("portfolio.brokerSync.checkWindow", { from: result.window.from, to: result.window.to })}
        </p>
      ) : null}
    </div>
  );
}
